import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type { SuggestDropdownPosition } from '../utils/gridSuggestDropdown';
import { formatSuggestCountLabel } from '../utils/gridSuggestDropdown';
import './GridSuggestOverlay.css';

export interface GridSuggestState {
  title: string;
  items: string[];
  /** 필터 후 전체 후보 수 (표시 상한 적용 전) */
  totalCount: number;
  row: number;
  col: number;
  position: SuggestDropdownPosition;
  activeIndex: number;
  query: string;
}

export interface GridSuggestOverlayHandle {
  update: (state: GridSuggestState) => void;
  hide: () => void;
  setActiveIndex: (index: number) => void;
}

interface Props {
  onSelect: (value: string) => void;
  onClose: () => void;
  onActiveIndexChange?: (index: number) => void;
}

const GridSuggestOverlay = forwardRef<GridSuggestOverlayHandle, Props>(({
  onSelect,
  onClose,
  onActiveIndexChange,
}, ref) => {
  const [state, setState] = useState<GridSuggestState | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollToActiveRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  const onActiveIndexChangeRef = useRef(onActiveIndexChange);
  onSelectRef.current = onSelect;
  onCloseRef.current = onClose;
  onActiveIndexChangeRef.current = onActiveIndexChange;

  useImperativeHandle(ref, () => ({
    update(next) {
      setState(next);
    },
    hide() {
      setState(null);
    },
    setActiveIndex(index) {
      // 키보드 이동 시에만 스크롤 (마우스 호버는 제외)
      scrollToActiveRef.current = true;
      setState(prev => (prev ? { ...prev, activeIndex: index } : prev));
      onActiveIndexChangeRef.current?.(index);
    },
  }), []);

  const handleActiveIndexChange = useCallback((index: number) => {
    setState(prev => (prev ? { ...prev, activeIndex: index } : prev));
    onActiveIndexChangeRef.current?.(index);
  }, []);

  useEffect(() => {
    if (!scrollToActiveRef.current) return;
    scrollToActiveRef.current = false;
    const list = listRef.current;
    if (!list || !state) return;
    const active = list.children[state.activeIndex] as HTMLElement | undefined;
    if (!active) return;
    const listTop = list.scrollTop;
    const listBottom = listTop + list.clientHeight;
    const itemTop = active.offsetTop;
    const itemBottom = itemTop + active.offsetHeight;
    if (itemBottom > listBottom) {
      list.scrollTop = itemBottom - list.clientHeight;
    } else if (itemTop < listTop) {
      list.scrollTop = itemTop;
    }
  }, [state]);

  if (!state || state.items.length === 0) return null;

  const highlight = (text: string) => {
    const q = state.query.trim();
    if (!q) return text;
    const lowerText = text.toLocaleLowerCase('en-US');
    const lowerQ = q.toLocaleLowerCase('en-US');
    const idx = lowerText.indexOf(lowerQ);
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="cd-grid-suggest__mark">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div
      className="cd-grid-suggest"
      style={{
        left: state.position.left,
        top: state.position.top,
        minWidth: state.position.minWidth,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="cd-grid-suggest__header">
        <span className="cd-grid-suggest__title">{state.title}</span>
        <span className="cd-grid-suggest__count">
          {formatSuggestCountLabel(state.items.length, state.totalCount)}
        </span>
        <button
          type="button"
          className="cd-grid-suggest__close"
          onClick={() => onCloseRef.current()}
          title="닫기"
          aria-label="닫기"
        >
          ×
        </button>
      </div>
      <div ref={listRef} className="cd-grid-suggest__list" style={{ maxHeight: state.position.maxHeight }}>
        {state.items.map((item, idx) => (
          <button
            key={`${item}-${idx}`}
            type="button"
            className={`cd-grid-suggest__item${idx === state.activeIndex ? ' cd-grid-suggest__item--active' : ''}`}
            onMouseEnter={() => handleActiveIndexChange(idx)}
            onMouseDown={() => onSelectRef.current(item)}
          >
            {highlight(item)}
          </button>
        ))}
      </div>
    </div>
  );
});

GridSuggestOverlay.displayName = 'GridSuggestOverlay';

export default GridSuggestOverlay;
